import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Shield, Upload, User, Building, Key, Globe, Users, History,
  RefreshCw, CheckCircle2, AlertCircle, Eye, EyeOff, Wifi, WifiOff, Radio,
  UserPlus, Trash2, Lock, Unlock, Copy, Monitor, Clock, ShieldCheck, Plus, Pencil, X, Check
} from 'lucide-react';
import axios from 'axios';
import PortalLayout from './PortalLayout';
import AccessControl from './AccessControl';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function PortalSettings() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('account');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', new_password: '', confirm: '' });
  const [showPasswords, setShowPasswords] = useState({});
  const token = localStorage.getItem('einvoice_token');

  // Account Edit
  const [editingAccount, setEditingAccount] = useState(false);
  const [accountForm, setAccountForm] = useState({ full_name: '', email: '', phone: '' });
  const [savingAccount, setSavingAccount] = useState(false);

  // Company Edit
  const [editingCompany, setEditingCompany] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    business_name: '', tin: '', brn: '', sst_number: '', ttx_number: '',
    bank_name: '', bank_account_number: '', msic_code: '',
    address_line1: '', city: '', state: '', postal_code: '',
    has_sst: true, default_tax_rate: 8, commencement_date: ''
  });
  const [savingCompany, setSavingCompany] = useState(false);

  // LHDN API
  const [lhdnCreds, setLhdnCreds] = useState({ client_id: '', client_secret: '', environment: 'sandbox', invoice_extraction_start_date: '' });
  const [lhdnStatus, setLhdnStatus] = useState(null);
  const [savingLhdn, setSavingLhdn] = useState(false);
  const [testingConn, setTestingConn] = useState(false);
  const [lhdnLogs, setLhdnLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotalPages, setLogsTotalPages] = useState(1);

  // Certificate
  const [certConfig, setCertConfig] = useState(null);
  const [savingCert, setSavingCert] = useState(false);

  // Users (kept for props to AccessControl)
  const [teamUsers, setTeamUsers] = useState([]);
  const [roles, setRoles] = useState({});

  // Access
  const [loginHistory, setLoginHistory] = useState([]);
  const [accessSettings, setAccessSettings] = useState({ session_timeout_minutes: 480, max_sessions: 5, ip_whitelist_enabled: false, ip_whitelist: [] });
  const [savingAccess, setSavingAccess] = useState(false);
  const [ipInput, setIpInput] = useState('');

  const ADMIN_EMAILS = ['jedeelooi@gmail.com', 'staymiihospitality@gmail.com'];
  const isAdmin = user && ADMIN_EMAILS.includes(user.email?.toLowerCase());

  const tabs = [
    { id: 'account', label: 'Account', icon: User },
    { id: 'company', label: 'Company', icon: Building },
    ...(isAdmin ? [
      { id: 'access_control', label: 'User Access', icon: ShieldCheck },
    ] : []),
    { id: 'lhdn_api', label: 'LHDN API', icon: Globe },
    { id: 'access', label: 'Access', icon: History },
    { id: 'security', label: 'Security', icon: Key },
  ];

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) { navigate('/einvoice/portal/auth'); return; }
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [profileRes, credsRes, certRes, usersRes, histRes, accessRes] = await Promise.all([
        axios.get(`${API_URL}/api/einvoice/portal/auth/me`, { headers }),
        axios.get(`${API_URL}/api/einvoice/lhdn-settings/credentials`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/einvoice/lhdn-settings/certificate`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/einvoice/user-management/users`, { headers }).catch(() => ({ data: { users: [], roles: {} } })),
        axios.get(`${API_URL}/api/einvoice/user-management/login-history?limit=30`, { headers }).catch(() => ({ data: { history: [] } })),
        axios.get(`${API_URL}/api/einvoice/user-management/access-settings`, { headers }).catch(() => ({ data: {} })),
      ]);
      setUser(profileRes.data);
      // Sync fresh profile data to localStorage so sidebar/header stays in sync
      try {
        const existing = JSON.parse(localStorage.getItem('einvoice_user') || '{}');
        localStorage.setItem('einvoice_user', JSON.stringify({ ...existing, ...profileRes.data }));
      } catch (e) { console.debug('localStorage cache write failed', e); }
      setLhdnStatus(credsRes.data);
      if (credsRes.data?.client_id) setLhdnCreds({ client_id: credsRes.data.client_id, client_secret: '', environment: credsRes.data.environment || 'sandbox', invoice_extraction_start_date: credsRes.data.invoice_extraction_start_date || '' });
      setCertConfig(certRes.data);
      setTeamUsers(usersRes.data.users || []);
      setRoles(usersRes.data.roles || {});
      setLoginHistory(histRes.data.history || []);
      if (accessRes.data?.session_timeout_minutes) setAccessSettings(accessRes.data);
    } catch (err) {
      if (err.response?.status === 401) navigate('/einvoice/portal/auth');
    } finally { setLoading(false); }
  };

  // Password
  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm) { toast.error('Passwords do not match'); return; }
    if (passwordForm.new_password.length < 8) { toast.error('Min 8 characters'); return; }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/einvoice/portal/auth/password`, { current_password: passwordForm.current, new_password: passwordForm.new_password }, { headers });
      toast.success('Password changed');
      setPasswordForm({ current: '', new_password: '', confirm: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  // Account Edit
  const handleEditAccount = () => {
    setAccountForm({ full_name: user?.full_name || '', email: user?.email || '', phone: user?.phone || '' });
    setEditingAccount(true);
  };

  const handleSaveAccount = async () => {
    if (!accountForm.email?.trim()) { toast.error('Email is required'); return; }
    if (!accountForm.full_name?.trim()) { toast.error('Full name is required'); return; }
    setSavingAccount(true);
    try {
      await axios.put(`${API_URL}/api/einvoice/portal/auth/profile`, accountForm, { headers });
      toast.success('Account updated');
      setEditingAccount(false);
      // Refresh user data and team users list
      const { data } = await axios.get(`${API_URL}/api/einvoice/portal/auth/me`, { headers });
      setUser(data);
      // Sync updated profile to localStorage so sidebar reflects changes immediately
      try {
        const existing = JSON.parse(localStorage.getItem('einvoice_user') || '{}');
        localStorage.setItem('einvoice_user', JSON.stringify({ ...existing, ...data }));
      } catch (e) { console.debug('localStorage cache write failed', e); }
      // Also refresh team users list to show updated email
      const usersRes = await axios.get(`${API_URL}/api/einvoice/user-management/users`, { headers }).catch(() => ({ data: { users: [] } }));
      setTeamUsers(usersRes.data.users || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update account'); }
    finally { setSavingAccount(false); }
  };

  // Company Edit
  const handleEditCompany = () => {
    const c = user?.company || {};
    setCompanyForm({
      business_name: c.business_name || c.name || '',
      tin: c.tin || '',
      brn: c.brn || '',
      sst_number: c.sst_number || c.sst_id || '',
      ttx_number: c.ttx_number || '',
      bank_name: c.bank_name || '',
      bank_account_number: c.bank_account_number || '',
      msic_code: c.msic_code || '',
      address_line1: c.address_line1 || '',
      city: c.city || '',
      state: c.state || c.state_name || '',
      postal_code: c.postal_code || '',
      has_sst: c.has_sst !== false,
      default_tax_rate: c.default_tax_rate || 8,
      commencement_date: c.commencement_date || ''
    });
    setEditingCompany(true);
  };

  const handleSaveCompany = async () => {
    if (!companyForm.business_name?.trim()) { toast.error('Company name is required'); return; }
    setSavingCompany(true);
    try {
      await axios.put(`${API_URL}/api/einvoice/portal/company`, companyForm, { headers });
      toast.success('Company details updated');
      setEditingCompany(false);
      // Refresh user data
      const { data } = await axios.get(`${API_URL}/api/einvoice/portal/auth/me`, { headers });
      setUser(data);
      try {
        const existing = JSON.parse(localStorage.getItem('einvoice_user') || '{}');
        localStorage.setItem('einvoice_user', JSON.stringify({ ...existing, ...data }));
      } catch (e) { console.debug('localStorage cache write failed', e); }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update company'); }
    finally { setSavingCompany(false); }
  };

  // LHDN
  const handleSaveLhdn = async () => {
    if (!lhdnCreds.client_id.trim()) { toast.error('Client ID required'); return; }
    if (!lhdnCreds.client_secret.trim() && !lhdnStatus?.configured) { toast.error('Client Secret required'); return; }
    setSavingLhdn(true);
    try {
      const selectedProp = localStorage.getItem('einvoice_selected_property');
      const payload = {
        client_id: lhdnCreds.client_id,
        client_secret: lhdnCreds.client_secret || 'keep-existing',
        environment: lhdnCreds.environment || 'sandbox',
        property_id: selectedProp || null,
        invoice_extraction_start_date: lhdnCreds.invoice_extraction_start_date || null
      };
      await axios.post(`${API_URL}/api/einvoice/lhdn-settings/credentials`, payload, { headers });
      toast.success(`LHDN credentials saved (${lhdnCreds.environment})`);
      const params = selectedProp ? `?property_id=${selectedProp}` : '';
      const { data } = await axios.get(`${API_URL}/api/einvoice/lhdn-settings/credentials${params}`, { headers });
      setLhdnStatus(data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSavingLhdn(false); }
  };

  const handleTestConnection = async () => {
    setTestingConn(true);
    try {
      const selectedProp = localStorage.getItem('einvoice_selected_property');
      const params = selectedProp ? `?property_id=${selectedProp}` : '';
      const { data } = await axios.post(`${API_URL}/api/einvoice/lhdn-settings/test-connection${params}`, {}, { headers });
      data.success ? toast.success(data.message) : toast.error(data.message);
      const r = await axios.get(`${API_URL}/api/einvoice/lhdn-settings/credentials${params}`, { headers });
      setLhdnStatus(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setTestingConn(false); }
  };

  const loadSubmissionLogs = async (pg = 1) => {
    setLogsLoading(true);
    try {
      const selectedProp = localStorage.getItem('einvoice_selected_property');
      const params = new URLSearchParams({ page: pg, limit: 10 });
      if (selectedProp) params.append('property_id', selectedProp);
      const { data } = await axios.get(`${API_URL}/api/einvoice/lhdn-settings/submission-logs?${params}`, { headers });
      setLhdnLogs(data.logs || []);
      setLogsPage(data.page || 1);
      setLogsTotalPages(Math.ceil((data.total || 0) / 10));
    } catch (err) { console.error('Failed to load logs:', err); }
    finally { setLogsLoading(false); }
  };

  const lhdnApiEndpoint = lhdnCreds.environment === 'production'
    ? 'https://api.myinvois.hasil.gov.my'
    : 'https://preprod-api.myinvois.hasil.gov.my';

  const handleCertChange = async (type) => {
    setSavingCert(true);
    try {
      await axios.post(`${API_URL}/api/einvoice/lhdn-settings/certificate`, { certificate_type: type }, { headers });
      const { data } = await axios.get(`${API_URL}/api/einvoice/lhdn-settings/certificate`, { headers });
      setCertConfig(data);
      toast.success(type === 'shared' ? 'Using shared certificate' : 'Switched to own certificate');
    } catch (err) { toast.error('Failed'); }
    finally { setSavingCert(false); }
  };

  // Access Settings
  const handleSaveAccess = async () => {
    setSavingAccess(true);
    try {
      await axios.post(`${API_URL}/api/einvoice/user-management/access-settings`, accessSettings, { headers });
      toast.success('Access settings saved');
    } catch (err) { toast.error('Failed'); }
    finally { setSavingAccess(false); }
  };

  const addIp = () => {
    if (ipInput.trim() && !accessSettings.ip_whitelist.includes(ipInput.trim())) {
      setAccessSettings({ ...accessSettings, ip_whitelist: [...accessSettings.ip_whitelist, ipInput.trim()] });
      setIpInput('');
    }
  };

  const removeIp = (ip) => setAccessSettings({ ...accessSettings, ip_whitelist: accessSettings.ip_whitelist.filter(i => i !== ip) });

  const formatTime = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso || '-'; } };

  const ROLE_COLORS = { admin: 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400', owner: 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400', manager: 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400', staff: 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400', viewer: 'bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-gray-300' };

  if (loading) {
    return <PortalLayout><div className="flex items-center justify-center h-[60vh]"><RefreshCw className="w-6 h-6 animate-spin text-[#4169E1]" /></div></PortalLayout>;
  }

  return (
    <PortalLayout>
      <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="portal-settings">
        <div>
          <h1 className="text-2xl font-bold text-[#4169E1]" data-testid="settings-title">Settings</h1>
          <p className="text-slate-500 dark:text-gray-400 text-sm mt-1">Manage account, team, LHDN integration, and access control</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-200 dark:border-zinc-800 overflow-x-auto" data-testid="settings-tabs">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id ? 'border-blue-600 text-[#4169E1]' : 'border-transparent text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:text-gray-200'}`}>
              <tab.icon className="w-4 h-4" />{tab.label}
            </button>
          ))}
        </div>

        {/* ========== ACCOUNT TAB ========== */}
        {activeTab === 'account' && (
          <Card data-testid="account-settings">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base text-[#4169E1]">Account Information</CardTitle>
              {!editingAccount && (
                <Button variant="outline" size="sm" onClick={handleEditAccount} data-testid="edit-account-btn">
                  Edit
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {editingAccount ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-slate-600 dark:text-gray-300">Full Name *</Label>
                      <Input 
                        value={accountForm.full_name} 
                        onChange={e => setAccountForm({...accountForm, full_name: e.target.value})}
                        placeholder="Enter full name"
                        data-testid="edit-fullname" 
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600 dark:text-gray-300">Email *</Label>
                      <Input 
                        type="email"
                        value={accountForm.email} 
                        onChange={e => setAccountForm({...accountForm, email: e.target.value})}
                        placeholder="Enter email"
                        data-testid="edit-email" 
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600 dark:text-gray-300">Phone</Label>
                      <Input 
                        value={accountForm.phone} 
                        onChange={e => setAccountForm({...accountForm, phone: e.target.value})}
                        placeholder="Enter phone number"
                        data-testid="edit-phone" 
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600 dark:text-gray-300">Role</Label>
                      <Input value={user?.role || 'admin'} readOnly className="bg-slate-50 dark:bg-zinc-900" />
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button onClick={handleSaveAccount} disabled={savingAccount} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-account-btn">
                      {savingAccount ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Save Changes
                    </Button>
                    <Button variant="outline" onClick={() => setEditingAccount(false)} data-testid="cancel-edit-btn">
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><Label className="text-sm text-slate-600 dark:text-gray-300">{user?.username ? 'Username' : 'Login Email'}</Label><Input value={user?.username || user?.email || ''} readOnly className="bg-slate-50 dark:bg-zinc-900" data-testid="settings-username" /></div>
                  <div><Label className="text-sm text-slate-600 dark:text-gray-300">Full Name</Label><Input value={user?.full_name || ''} readOnly className="bg-slate-50 dark:bg-zinc-900" data-testid="settings-fullname" /></div>
                  <div><Label className="text-sm text-slate-600 dark:text-gray-300">Email</Label><Input value={user?.email || ''} readOnly className="bg-slate-50 dark:bg-zinc-900" data-testid="settings-email" /></div>
                  <div><Label className="text-sm text-slate-600 dark:text-gray-300">Phone</Label><Input value={user?.phone || ''} readOnly className="bg-slate-50 dark:bg-zinc-900" data-testid="settings-phone" /></div>
                  <div><Label className="text-sm text-slate-600 dark:text-gray-300">Role</Label><Input value={user?.role || 'admin'} readOnly className="bg-slate-50 dark:bg-zinc-900" data-testid="settings-role" /></div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ========== COMPANY TAB ========== */}
        {activeTab === 'company' && (
          <Card data-testid="company-settings">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base text-[#4169E1]">Company Details</CardTitle>
              {user?.company && !editingCompany && (
                <Button variant="outline" size="sm" onClick={handleEditCompany} data-testid="edit-company-btn">
                  Edit
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {editingCompany ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-slate-600 dark:text-gray-300">Company Name *</Label>
                      <Input value={companyForm.business_name} onChange={e => setCompanyForm({...companyForm, business_name: e.target.value})} data-testid="edit-company-name" />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600 dark:text-gray-300">TIN (Tax ID)</Label>
                      <Input value={companyForm.tin} onChange={e => setCompanyForm({...companyForm, tin: e.target.value})} placeholder="e.g. C26243472050" data-testid="edit-company-tin" />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600 dark:text-gray-300">BRN (Business Reg No.)</Label>
                      <Input value={companyForm.brn} onChange={e => setCompanyForm({...companyForm, brn: e.target.value})} placeholder="e.g. 202001003078" data-testid="edit-company-brn" />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600 dark:text-gray-300">SST No</Label>
                      <Input value={companyForm.sst_number} onChange={e => setCompanyForm({...companyForm, sst_number: e.target.value})} placeholder="e.g. P11-2105-32000004" data-testid="edit-company-sst" />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600 dark:text-gray-300">Tourism Tax No (TTX)</Label>
                      <Input value={companyForm.ttx_number} onChange={e => setCompanyForm({...companyForm, ttx_number: e.target.value})} placeholder="e.g. 072-2020-10000019" data-testid="edit-company-ttx" />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600 dark:text-gray-300">MSIC Code</Label>
                      <Input value={companyForm.msic_code} onChange={e => setCompanyForm({...companyForm, msic_code: e.target.value})} placeholder="e.g. 55101" data-testid="edit-company-msic" />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600 dark:text-gray-300">Bank Name</Label>
                      <Input value={companyForm.bank_name} onChange={e => setCompanyForm({...companyForm, bank_name: e.target.value})} placeholder="e.g. CIMB Bank" data-testid="edit-company-bank-name" />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600 dark:text-gray-300">Bank Account No.</Label>
                      <Input value={companyForm.bank_account_number} onChange={e => setCompanyForm({...companyForm, bank_account_number: e.target.value})} placeholder="e.g. 8010347443" data-testid="edit-company-bank-account" />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-sm text-slate-600 dark:text-gray-300">Address</Label>
                      <Input value={companyForm.address_line1} onChange={e => setCompanyForm({...companyForm, address_line1: e.target.value})} placeholder="Street address" data-testid="edit-company-address" />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600 dark:text-gray-300">City</Label>
                      <Input value={companyForm.city} onChange={e => setCompanyForm({...companyForm, city: e.target.value})} placeholder="e.g. Georgetown" data-testid="edit-company-city" />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600 dark:text-gray-300">State</Label>
                      <Input value={companyForm.state} onChange={e => setCompanyForm({...companyForm, state: e.target.value})} placeholder="e.g. Penang" data-testid="edit-company-state" />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600 dark:text-gray-300">Postal Code</Label>
                      <Input value={companyForm.postal_code} onChange={e => setCompanyForm({...companyForm, postal_code: e.target.value})} placeholder="e.g. 10050" data-testid="edit-company-postal" />
                    </div>
                  </div>
                  
                  {/* Tax Settings Section */}
                  <div className="mt-6 pt-4 border-t border-slate-200 dark:border-zinc-700">
                    <h4 className="text-sm font-semibold text-slate-700 dark:text-gray-300 mb-4">Business Commencement & Tax Settings</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm text-slate-600 dark:text-gray-300">Business Commencement Date</Label>
                        <Input type="date" value={companyForm.commencement_date} onChange={e => setCompanyForm({...companyForm, commencement_date: e.target.value})} data-testid="edit-commencement-date" />
                        <p className="text-xs text-slate-400 dark:text-gray-500 mt-1">Official business start date. Controls Beds24 sync cutoff.</p>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-zinc-800/50 rounded-lg">
                        <div>
                          <Label className="text-sm text-slate-700 dark:text-gray-300">SST Registered</Label>
                          <p className="text-xs text-slate-500 dark:text-gray-400">Does your company have SST registration?</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setCompanyForm({...companyForm, has_sst: !companyForm.has_sst})}
                          className={`relative w-12 h-6 rounded-full transition-colors ${companyForm.has_sst ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-zinc-600'}`}
                          data-testid="toggle-sst"
                        >
                          <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${companyForm.has_sst ? 'translate-x-7' : 'translate-x-1'}`} />
                        </button>
                      </div>
                      <div>
                        <Label className="text-sm text-slate-600 dark:text-gray-300">Default Tax Rate (%)</Label>
                        <Input 
                          type="number" 
                          value={companyForm.default_tax_rate} 
                          onChange={e => setCompanyForm({...companyForm, default_tax_rate: parseFloat(e.target.value) || 0})} 
                          placeholder="e.g. 8" 
                          min="0" 
                          max="100"
                          data-testid="edit-company-tax-rate" 
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 pt-4">
                    <Button onClick={handleSaveCompany} disabled={savingCompany} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-company-btn">
                      {savingCompany ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Save Changes
                    </Button>
                    <Button variant="outline" onClick={() => setEditingCompany(false)} data-testid="cancel-company-edit-btn">
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : user?.company ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><Label className="text-sm text-slate-600 dark:text-gray-300">Company Name</Label><Input value={user.company.name || user.company.business_name || ''} readOnly className="bg-slate-50 dark:bg-zinc-900" data-testid="company-name" /></div>
                    <div><Label className="text-sm text-slate-600 dark:text-gray-300">TIN</Label><Input value={user.company.tin || ''} readOnly className="bg-slate-50 dark:bg-zinc-900" data-testid="company-tin" /></div>
                    <div><Label className="text-sm text-slate-600 dark:text-gray-300">BRN</Label><Input value={user.company.brn || ''} readOnly className="bg-slate-50 dark:bg-zinc-900" data-testid="company-brn" /></div>
                    <div><Label className="text-sm text-slate-600 dark:text-gray-300">SST No</Label><Input value={user.company.sst_number || user.company.sst_id || '-'} readOnly className="bg-slate-50 dark:bg-zinc-900" data-testid="company-sst" /></div>
                    <div><Label className="text-sm text-slate-600 dark:text-gray-300">Tourism Tax No (TTX)</Label><Input value={user.company.ttx_number || '-'} readOnly className="bg-slate-50 dark:bg-zinc-900" data-testid="company-ttx" /></div>
                    <div><Label className="text-sm text-slate-600 dark:text-gray-300">MSIC Code</Label><Input value={user.company.msic_code || ''} readOnly className="bg-slate-50 dark:bg-zinc-900" data-testid="company-msic" /></div>
                    <div><Label className="text-sm text-slate-600 dark:text-gray-300">Bank Name</Label><Input value={user.company.bank_name || ''} readOnly className="bg-slate-50 dark:bg-zinc-900" data-testid="company-bank-name" /></div>
                    <div><Label className="text-sm text-slate-600 dark:text-gray-300">Bank Account No.</Label><Input value={user.company.bank_account_number || ''} readOnly className="bg-slate-50 dark:bg-zinc-900" data-testid="company-bank-account" /></div>
                    <div><Label className="text-sm text-slate-600 dark:text-gray-300">Business Commencement Date</Label><Input value={user.company.commencement_date ? new Date(user.company.commencement_date + 'T00:00:00').toLocaleDateString('en-MY', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'} readOnly className="bg-slate-50 dark:bg-zinc-900" data-testid="company-commencement-date" /></div>
                  </div>
                  
                  {/* Tax Settings Display */}
                  <div className="pt-4 border-t border-slate-200 dark:border-zinc-700">
                    <h4 className="text-sm font-semibold text-slate-700 dark:text-gray-300 mb-3">Tax Settings</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-zinc-800/50 rounded-lg">
                        <span className="text-sm text-slate-600 dark:text-gray-300">SST Registered</span>
                        <span className={`text-sm font-medium ${user.company.has_sst !== false ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500 dark:text-gray-400'}`}>
                          {user.company.has_sst !== false ? 'Yes' : 'No'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-zinc-800/50 rounded-lg">
                        <span className="text-sm text-slate-600 dark:text-gray-300">Default Tax Rate</span>
                        <span className="text-sm font-medium text-slate-800 dark:text-white">{user.company.default_tax_rate || 8}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Building className="w-10 h-10 mx-auto text-slate-300 dark:text-gray-600 mb-3" />
                  <p className="text-sm text-slate-500 dark:text-gray-400">Company not configured</p>
                  <Button size="sm" className="mt-3" onClick={() => navigate('/einvoice/portal/onboarding')}>Complete Setup</Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ========== ACCESS CONTROL TAB ========== */}
        {activeTab === 'access_control' && (
          <AccessControl token={token} user={user} teamUsers={teamUsers} roles={roles} onRefresh={loadAll} />
        )}

        {/* ========== LHDN API TAB ========== */}
        {activeTab === 'lhdn_api' && (
          <div className="space-y-4" data-testid="lhdn-api-settings">
            {/* Status Banner */}
            <div className={`flex items-center gap-3 p-4 rounded-lg ${lhdnStatus?.configured ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800' : 'bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800'}`}>
              {lhdnStatus?.configured ? (
                <><Wifi className="w-5 h-5 text-green-600 dark:text-green-400" /><div><p className="font-medium text-green-700 dark:text-green-300">LHDN API Configured</p><p className="text-xs text-green-600 dark:text-green-400">Client ID: {lhdnStatus.client_id} | {(lhdnStatus.environment || 'sandbox').toUpperCase()}</p></div></>
              ) : (
                <><WifiOff className="w-5 h-5 text-amber-600 dark:text-amber-400" /><div><p className="font-medium text-amber-700 dark:text-amber-300">LHDN API Not Configured</p><p className="text-xs text-amber-600 dark:text-amber-400">Enter your MyInvois API credentials</p></div></>
              )}
            </div>

            {/* Credentials Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2 text-[#4169E1]"><Globe className="w-4 h-4" /> LHDN MyInvois API</CardTitle>
                <CardDescription className="text-xs">Register Staymii as an ERP system in your MyInvois portal to obtain Client ID and Client Secret.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="max-w-lg space-y-4">
                  {/* Environment */}
                  <div>
                    <Label className="text-sm text-slate-600 dark:text-gray-300">Environment</Label>
                    <Select value={lhdnCreds.environment} onValueChange={v => setLhdnCreds({ ...lhdnCreds, environment: v })}>
                      <SelectTrigger className="w-full" data-testid="lhdn-environment"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sandbox">Sandbox (Testing)</SelectItem>
                        <SelectItem value="production">Production (Live)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-400 dark:text-gray-500 mt-1 font-mono" data-testid="lhdn-api-endpoint">{lhdnApiEndpoint}</p>
                  </div>

                  {/* Client ID */}
                  <div>
                    <Label className="text-sm text-slate-600 dark:text-gray-300">Client ID</Label>
                    <Input value={lhdnCreds.client_id} onChange={e => setLhdnCreds({ ...lhdnCreds, client_id: e.target.value })} placeholder="LHDN Client ID" data-testid="lhdn-client-id" />
                  </div>

                  {/* Client Secret */}
                  <div>
                    <Label className="text-sm text-slate-600 dark:text-gray-300">Client Secret</Label>
                    <div className="relative">
                      <Input type={showPasswords.lhdn ? 'text' : 'password'} value={lhdnCreds.client_secret} onChange={e => setLhdnCreds({ ...lhdnCreds, client_secret: e.target.value })}
                        placeholder={lhdnStatus?.configured ? 'Leave blank to keep existing' : 'Enter Client Secret'} data-testid="lhdn-client-secret" />
                      <button type="button" onClick={() => setShowPasswords({ ...showPasswords, lhdn: !showPasswords.lhdn })} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:hover:text-gray-300">
                        {showPasswords.lhdn ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Booking Sync Start Date */}
                  <div>
                    <Label className="text-sm text-slate-600 dark:text-gray-300">Booking Sync Start Date (Arrival Based)</Label>
                    <Input type="date" value={lhdnCreds.invoice_extraction_start_date} onChange={e => setLhdnCreds({ ...lhdnCreds, invoice_extraction_start_date: e.target.value })} data-testid="extraction-start-date" />
                    <p className="text-xs text-slate-400 dark:text-gray-500 mt-1">Only bookings with <strong>arrival date on or after</strong> this date will be synced from Beds24. The system uses the later of this date and the Business Commencement Date from your company profile.</p>
                  </div>

                  {/* Beds24 Property ID (read-only from PMS connection) */}
                  {lhdnStatus?.beds24_property_id && (
                    <div>
                      <Label className="text-sm text-slate-600 dark:text-gray-300">Beds24 Property ID</Label>
                      <Input value={lhdnStatus.beds24_property_id} readOnly className="bg-slate-50 dark:bg-zinc-900 cursor-default" data-testid="beds24-property-id" />
                      <p className="text-xs text-slate-400 dark:text-gray-500 mt-1">Linked from PMS Integration settings.</p>
                    </div>
                  )}

                  {/* Monthly Consolidated Submission Info */}
                  <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                    <p className="text-xs font-medium text-amber-800 dark:text-amber-300 mb-1">Monthly Consolidated e-Invoice Deadline</p>
                    <p className="text-xs text-amber-700 dark:text-amber-400">
                      LHDN requires all B2C consolidated e-Invoices to be submitted <strong>before the 15th of the following month</strong>.
                      The system groups transactions by calendar month and prepares consolidated invoices accordingly.
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-500 mt-1 font-mono">
                      Example: March 2026 transactions must be submitted before 15 April 2026.
                    </p>
                  </div>

                  {/* Info Box */}
                  <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                    <p className="text-xs text-blue-700 dark:text-blue-400">
                      <strong>OAuth Configuration:</strong> The system uses <code className="bg-blue-100 dark:bg-blue-900/50 px-1 rounded text-[11px]">POST /connect/token</code> with <code className="bg-blue-100 dark:bg-blue-900/50 px-1 rounded text-[11px]">grant_type=client_credentials</code> and <code className="bg-blue-100 dark:bg-blue-900/50 px-1 rounded text-[11px]">scope=InvoicingAPI</code> to authenticate.
                    </p>
                  </div>

                  {/* Last Test Status */}
                  {lhdnStatus?.last_tested && (
                    <div className="flex items-center gap-2 text-xs">
                      {lhdnStatus.test_status === 'success' ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> : <AlertCircle className="w-3.5 h-3.5 text-amber-500" />}
                      <span className="text-slate-500 dark:text-gray-400">Last tested: {new Date(lhdnStatus.last_tested).toLocaleString('en-MY')}</span>
                      <Badge className={`text-[10px] ${lhdnStatus.test_status === 'success' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'}`}>
                        {(lhdnStatus.test_status || 'untested').toUpperCase()}
                      </Badge>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3 pt-2">
                    <Button onClick={handleSaveLhdn} disabled={savingLhdn} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-lhdn-btn">
                      {savingLhdn && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />} Save Credentials
                    </Button>
                    {lhdnStatus?.configured && (
                      <Button variant="outline" onClick={handleTestConnection} disabled={testingConn} data-testid="test-lhdn-btn">
                        {testingConn ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Radio className="w-4 h-4 mr-2" />} Test Connection
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Submission Logs */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2 text-[#4169E1]"><History className="w-4 h-4" /> LHDN Submission Log</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => loadSubmissionLogs(1)} disabled={logsLoading} data-testid="refresh-logs-btn">
                    <RefreshCw className={`w-4 h-4 ${logsLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
                <CardDescription className="text-xs">Track invoice submissions, validation status, and troubleshoot errors.</CardDescription>
              </CardHeader>
              <CardContent>
                {lhdnLogs.length === 0 && !logsLoading ? (
                  <div className="text-center py-8 text-slate-400 dark:text-gray-500">
                    <History className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No submission logs yet</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={() => loadSubmissionLogs(1)} data-testid="load-logs-btn">Load Logs</Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b dark:border-gray-700">
                          <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 dark:text-gray-400">Invoice #</th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 dark:text-gray-400">Action</th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 dark:text-gray-400">Submission UID</th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 dark:text-gray-400">Status</th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 dark:text-gray-400">Timestamp</th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 dark:text-gray-400">Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lhdnLogs.map((log, i) => (
                          <tr key={i} className="border-b dark:border-gray-800 hover:bg-slate-50 dark:hover:bg-white/5" data-testid={`log-row-${i}`}>
                            <td className="px-3 py-2 font-mono text-xs">{log.invoice_number || '-'}</td>
                            <td className="px-3 py-2">
                              <Badge className={`text-[10px] ${log.action === 'lhdn_submit' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' : log.action === 'lhdn_check_status' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}>
                                {log.action === 'lhdn_submit' ? 'Submit' : log.action === 'lhdn_check_status' ? 'Check Status' : log.action === 'generate_invoice' ? 'Generate' : log.action}
                              </Badge>
                            </td>
                            <td className="px-3 py-2 font-mono text-xs max-w-[120px] truncate">{log.submission_uid || '-'}</td>
                            <td className="px-3 py-2">
                              {log.validation_status ? (
                                <Badge className={`text-[10px] ${log.validation_status === 'valid' || log.validation_status === 'success' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : log.validation_status === 'error' || log.validation_status === 'failed' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'}`}>
                                  {log.validation_status.toUpperCase()}
                                </Badge>
                              ) : <span className="text-slate-400 text-xs">-</span>}
                            </td>
                            <td className="px-3 py-2 text-xs text-slate-500 dark:text-gray-400 whitespace-nowrap">
                              {log.timestamp ? new Date(log.timestamp).toLocaleString('en-MY', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                            </td>
                            <td className="px-3 py-2 text-xs text-red-500 max-w-[200px] truncate">{log.error_message || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {logsTotalPages > 1 && (
                      <div className="flex items-center justify-between mt-3 pt-3 border-t dark:border-gray-700">
                        <span className="text-xs text-slate-400">Page {logsPage}/{logsTotalPages}</span>
                        <div className="flex gap-1">
                          <Button variant="outline" size="sm" onClick={() => { setLogsPage(p => Math.max(1, p - 1)); loadSubmissionLogs(Math.max(1, logsPage - 1)); }} disabled={logsPage === 1}>Prev</Button>
                          <Button variant="outline" size="sm" onClick={() => { setLogsPage(p => Math.min(logsTotalPages, p + 1)); loadSubmissionLogs(Math.min(logsTotalPages, logsPage + 1)); }} disabled={logsPage === logsTotalPages}>Next</Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ========== ACCESS TAB ========== */}
        {activeTab === 'access' && (
          <div className="space-y-4" data-testid="access-settings">
            {/* Login History */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2 text-[#4169E1]"><History className="w-4 h-4" /> Login History</CardTitle>
                <CardDescription>Recent login activity for your organization</CardDescription>
              </CardHeader>
              <CardContent>
                {loginHistory.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="login-history-table">
                      <thead>
                        <tr className="border-b bg-slate-50 dark:bg-zinc-900">
                          <th className="text-left px-4 py-2 text-xs text-slate-500 dark:text-gray-400 uppercase">Action</th>
                          <th className="text-left px-4 py-2 text-xs text-slate-500 dark:text-gray-400 uppercase">Email</th>
                          <th className="text-left px-4 py-2 text-xs text-slate-500 dark:text-gray-400 uppercase">IP Address</th>
                          <th className="text-left px-4 py-2 text-xs text-slate-500 dark:text-gray-400 uppercase">Device</th>
                          <th className="text-left px-4 py-2 text-xs text-slate-500 dark:text-gray-400 uppercase">Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loginHistory.map((h, i) => (
                          <tr key={i} className="border-b border-slate-200 dark:border-zinc-800 hover:bg-slate-50 dark:hover:bg-zinc-900">
                            <td className="px-4 py-3">
                              <Badge variant="outline" className={`text-xs capitalize ${h.action === 'login' ? 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400' : 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400'}`}>
                                {h.action === 'invite_user' ? 'Invited' : h.action}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-slate-700 dark:text-gray-200">{h.email || h.target_email || '-'}</td>
                            <td className="px-4 py-3 font-mono text-xs text-slate-500 dark:text-gray-400">{h.ip_address || '-'}</td>
                            <td className="px-4 py-3 text-xs text-slate-500 dark:text-gray-400 max-w-[200px] truncate">{h.user_agent ? h.user_agent.split(' ').slice(0, 3).join(' ') : '-'}</td>
                            <td className="px-4 py-3 text-xs text-slate-500 dark:text-gray-400">{formatTime(h.timestamp)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500 dark:text-gray-400">
                    <Clock className="w-8 h-8 mx-auto text-slate-300 dark:text-gray-600 mb-2" />
                    <p className="text-sm">No login history yet</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Access Control Settings */}
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2 text-[#4169E1]"><Monitor className="w-4 h-4" /> Access Control</CardTitle></CardHeader>
              <CardContent className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-slate-600 dark:text-gray-300">Session Timeout (minutes)</Label>
                    <Input type="number" min={15} max={10080} value={accessSettings.session_timeout_minutes}
                      onChange={e => setAccessSettings({ ...accessSettings, session_timeout_minutes: parseInt(e.target.value) || 480 })}
                      data-testid="session-timeout" />
                    <p className="text-xs text-slate-400 dark:text-gray-500 mt-1">Auto-logout after inactivity (15 min - 7 days)</p>
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600 dark:text-gray-300">Max Concurrent Sessions</Label>
                    <Input type="number" min={1} max={20} value={accessSettings.max_sessions}
                      onChange={e => setAccessSettings({ ...accessSettings, max_sessions: parseInt(e.target.value) || 5 })}
                      data-testid="max-sessions" />
                    <p className="text-xs text-slate-400 dark:text-gray-500 mt-1">Limit active sessions per user</p>
                  </div>
                </div>

                {/* IP Whitelist */}
                <div className="p-4 border border-slate-200 dark:border-zinc-800 rounded-xl">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-slate-900 dark:text-white text-sm">IP Whitelist</h4>
                      <p className="text-xs text-slate-500 dark:text-gray-400">Restrict access to specific IP addresses</p>
                    </div>
                    <Switch checked={accessSettings.ip_whitelist_enabled}
                      onCheckedChange={v => setAccessSettings({ ...accessSettings, ip_whitelist_enabled: v })}
                      data-testid="toggle-ip-whitelist" />
                  </div>
                  {accessSettings.ip_whitelist_enabled && (
                    <div className="space-y-3 pt-2 border-t border-slate-200 dark:border-zinc-800">
                      <div className="flex gap-2">
                        <Input value={ipInput} onChange={e => setIpInput(e.target.value)} placeholder="e.g. 192.168.1.0/24" className="flex-1" data-testid="ip-input"
                          onKeyDown={e => e.key === 'Enter' && addIp()} />
                        <Button variant="outline" size="sm" onClick={addIp} data-testid="add-ip-btn">Add</Button>
                      </div>
                      {accessSettings.ip_whitelist.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {accessSettings.ip_whitelist.map(ip => (
                            <Badge key={ip} variant="outline" className="text-xs font-mono gap-1">
                              {ip}
                              <button onClick={() => removeIp(ip)} className="ml-1 text-slate-400 dark:text-gray-500 hover:text-red-600 dark:text-red-400">&times;</button>
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <Button onClick={handleSaveAccess} disabled={savingAccess} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-access-btn">
                  {savingAccess && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />} Save Access Settings
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'security' && (
          <Card data-testid="security-settings">
            <CardHeader className="pb-3"><CardTitle className="text-base text-[#4169E1]">Change Password</CardTitle></CardHeader>
            <CardContent>
              <div className="max-w-md space-y-4">
                <div><Label className="text-sm text-slate-600 dark:text-gray-300">Current Password</Label>
                  <div className="relative"><Input type={showPasswords.current ? 'text' : 'password'} value={passwordForm.current}
                    onChange={e => setPasswordForm({ ...passwordForm, current: e.target.value })} data-testid="input-current-password" />
                    <button type="button" onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:text-gray-300">
                      {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button></div></div>
                <div><Label className="text-sm text-slate-600 dark:text-gray-300">New Password</Label>
                  <div className="relative"><Input type={showPasswords.new_password ? 'text' : 'password'} value={passwordForm.new_password}
                    onChange={e => setPasswordForm({ ...passwordForm, new_password: e.target.value })} data-testid="input-new-password" />
                    <button type="button" onClick={() => setShowPasswords({ ...showPasswords, new_password: !showPasswords.new_password })}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:text-gray-300">
                      {showPasswords.new_password ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button></div></div>
                <div><Label className="text-sm text-slate-600 dark:text-gray-300">Confirm New Password</Label>
                  <Input type="password" value={passwordForm.confirm} onChange={e => setPasswordForm({ ...passwordForm, confirm: e.target.value })} data-testid="input-confirm-password" /></div>
                <Button onClick={handleChangePassword} disabled={saving || !passwordForm.current || !passwordForm.new_password || !passwordForm.confirm}
                  className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="change-password-btn">
                  {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />} Change Password
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PortalLayout>
  );
}
